<template>
  <v-dialog v-model="show" persistent max-width="450">
    <v-card>
      <v-card-title>
        <v-icon :color="recording ? 'red' : ''">mdi-microphone</v-icon>
        <div class="ml-2">
          {{ title }}
        </div>
        <v-spacer></v-spacer>
        <v-btn icon class="ma-0" @click="cancel">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pt-4 pb-8">
        <v-alert
          v-show="recording"
          color="red"
          text
          prominent
          dark
        >
          <div class="d-flex justify-start mt-4">
            <div class="mr-4">
              <v-btn rounded :loading="loading" @click="toggleRec" :disabled="!micEnabled" color="red">
                <v-icon>
                  mdi-stop
                </v-icon>
              </v-btn>
            </div>
            <div class="d-flex flex-column">
              <av-media :media="stream" type="frequ" :line-width="6"
                :frequ-line-cap="true" :frequ-lnum="16" line-color="red"
                :canv-width="220" :canv-height="35">
              </av-media>
              <div class="font-weight-thin">
                {{ recTime | formatTime }}
              </div>
            </div>
          </div>
        </v-alert>
        <v-row align="center" justify="center" v-show="!recording && !recSrc">
          <v-btn rounded :loading="loading" @click="toggleRec" :disabled="!micEnabled">
            <v-icon left color="red" class="mr-1">
              mdi-{{ recording ? 'stop' : 'radiobox-marked' }}
            </v-icon>
            <span class="font-weight-bold">
              {{ recording ? 'STOP' : 'REC&nbsp;' }}
            </span>
          </v-btn>
        </v-row>
        <v-row align="center" justify="center" v-show="recSrc">
          <audio-player :src="recSrc" v-if="recSrc" style="width: 302px" class="mr-4" />
          <v-btn icon @click="recSrc=null" :disabled="!recSrc">
            <v-icon color="red">mdi-delete</v-icon>
          </v-btn>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="cancel" text>
          {{ $t('vmail.cfg.greeting.btnCancel') }}
        </v-btn>
        <v-btn :disabled="!canSave" @click="saveRec" color="primary" :dark="canSave" text>
          {{ $t('vmail.cfg.greeting.btnSave') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import events from 'Store/events'
import LocalStorage from 'Store/class/local_storage'
import BrowserMedia from 'Store/sipua/browser_media'
import RecProc from './recproc'
import AudioPlayer from '@/components/audio_player'

export default {
  name: 'vmail-greeting-recorder',
  components: { AudioPlayer },
  data () {
    return {
      recording: false,
      loading: false,
      stream: null,
      rec: null,
      recTime: 0,
      timer: null,
      recSrc: null,
      micEnabled: true
    }
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Recording title not provided!'
    }
  },
  computed: {
    ...mapGetters({ media: 'sipua/browserMedia' }),
    canSave: function () {
      return this.recSrc !== null
    }
  },
  watch: {
    show: function (val) {
      if (val) {
        // reset
        this.loading = false
        this.recSrc = null
      }
    }
  },
  methods: {
    recStart: async function () {
      this.recSrc = null
      this.loading = true

      let inputDevs = this.media.input
      let devIn = null
      let safariWorkaround = false
      if (inputDevs.length === 0) {
        // this is likely because there are browsers that don't allow media selection, for example Safari.
        // First, let's retrieve the list of input devices by querying them again.
        const media = new BrowserMedia()
        await media.setDevices()
        inputDevs = media.getInputDevs()
        if (inputDevs.length === 0) {
          // We definitely have no input devices. Turning off the recording.
          this.micEnabled = false
          this.dbg('No input devices found.')
          events.pubError(this.$t('vmail.cfg.greeting.rec.failMediaMsg'))
          return
        }
        devIn = LocalStorage.matchInputDevice(inputDevs)
        if (devIn && devIn.id === 'default') {
          // Since in this case media selection is not available, we have to select one of the input devices.
          devIn = inputDevs[0]
          safariWorkaround = true
        }
      } else {
        devIn = LocalStorage.matchInputDevice(inputDevs)
      }
      this.dbg(`Setting recording device to '${devIn.label}', id: ${devIn.id}`)
      const constraints = { video: false, audio: { deviceId: { exact: devIn.id } } }
      navigator.mediaDevices.getUserMedia(constraints)
        .then(stream => {
          const onStart = () => {
            this.recording = true
            this.loading = false
            this.$root.$emit('pause-audio')
            this.timer = setInterval(() => { this.recTime++ }, 1000)
          }

          const onStop = () => {
            this.recording = false
            clearInterval(this.timer)
            this.recTime = 0
            this.recSrc = this.rec.playerSrcURL()
            this.loading = false
          }

          this.stream = stream
          this.rec = RecProc(stream, onStart, onStop, safariWorkaround)
          this.rec.start()
        })
        .catch(err => {
          this.dbg('Failed recording: %o', err)
          let msg = `Failed get user media: ${err}`
          if (err.name === 'OverconstrainedError') {
            msg = `Failed to use recording device '${devIn.label}'. Check if device is connected.`
          } else if (err.name === 'NotAllowedError') {
            msg = this.$t('vmail.cfg.greeting.rec.failMediaPermission')
            this.micEnabled = false
          }
          this.recording = false
          events.pubError(msg)
          this.recStop()
        })
    },

    recStop: function () {
      this.recTime = 0
      this.loading = true
      if (this.rec) {
        this.rec.stop()
      }
    },

    toggleRec: function () {
      if (this.recording) {
        this.recStop()
      } else {
        this.recStart()
      }
    },

    cancel: function () {
      this.recStop()
      this.loading = false
      this.$root.$emit('pause-audio')
      this.$emit('cancel')
    },

    emitRestore: function () {
      this.recStop()
      this.loading = false
      this.$root.$emit('pause-audio')
      this.$emit('restore-archive')
      this.$emit('cancel')
    },

    saveRec: function () {
      this.$root.$emit('pause-audio')
      if (!this.recSrc) {
        this.dbg('ERROR: save command available while no recording blob')
        return
      }
      this.$emit('save-record', new Blob(this.rec.recChunks, { type: this.rec.mimeType }))
    }
  }
}
</script>

<style scoped>
</style>
